// Offline write queue — persisted to localStorage, replayed on network return.
// Pragmatic V1: handles POST/PUT/PATCH/DELETE only. Idempotency depends on backend.

const QUEUE_KEY = "ss_offline_queue_v1";

function readQueue() {
  try {
    const raw = localStorage.getItem(QUEUE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function writeQueue(q) {
  try {
    localStorage.setItem(QUEUE_KEY, JSON.stringify(q));
    notifyChange();
  } catch {}
}

const listeners = new Set();
function notifyChange() {
  const q = readQueue();
  listeners.forEach((fn) => {
    try { fn(q); } catch {}
  });
}

export function subscribeQueue(fn) {
  listeners.add(fn);
  fn(readQueue());
  return () => listeners.delete(fn);
}

export function enqueue(item) {
  const q = readQueue();
  q.push({
    id: `q_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
    method: item.method,
    url: item.url,
    data: item.data,
    headers: item.headers || {},
    created_at: new Date().toISOString(),
    attempts: 0,
  });
  writeQueue(q);
}

export function getQueue() {
  return readQueue();
}

export function clearQueue() {
  writeQueue([]);
}

let syncing = false;
export async function replayQueue(axiosInstance) {
  if (syncing) return { synced: 0, failed: 0, queued: readQueue().length };
  syncing = true;
  let synced = 0;
  let failed = 0;
  try {
    const q = readQueue();
    if (q.length === 0) return { synced, failed, queued: 0 };
    const remaining = [];
    for (const item of q) {
      try {
        await axiosInstance.request({
          method: item.method,
          url: item.url,
          data: item.data,
          headers: item.headers,
        });
        synced++;
      } catch (e) {
        const status = e?.response?.status;
        // 4xx (other than 0/network) = drop (bad request, dup, etc.) ; 5xx + network = keep
        if (status && status >= 400 && status < 500) {
          failed++;
        } else {
          item.attempts = (item.attempts || 0) + 1;
          remaining.push(item);
        }
      }
    }
    writeQueue(remaining);
    return { synced, failed, queued: remaining.length };
  } finally {
    syncing = false;
  }
}
